import { useEffect, useMemo, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { useNavigate } from "@tanstack/react-router";
import { AlertCircle, Check, CloudOff, Eye, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import { usePermissions } from "@/hooks/use-permissions";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useProjectPlans } from "@/components/plans/plans-view/use-project-plans";
import { useGenerateReport } from "@/components/records/report-generation-dialog/use-generate-report";
import { ApiError } from "@/lib/api";
import { useOnlineStatus } from "@/offline/online-status";
import type { ProjectDetail } from "@/types/api";
import { clearPreconSelection, readPreconSelection } from "./precon-handoff";
import { readPreconDraft, writePreconDraft } from "./precon-draft";
import type { PreconSectionDraft } from "./precon-draft";
import { derivePreconSections } from "./derive-precon-sections";
import { prefillPreconDate } from "./prefill-precon-date";
import { buildPreconPayload } from "./build-precon-payload";
import { parsePreconErrors, type PreconFieldErrors } from "./precon-errors";
import { PreconCoverStep } from "./precon-cover-step";
import { PreconSectionsStep } from "./precon-sections-step";
import { PreconReviewStep } from "./precon-review-step";
import { PreconPreview } from "./precon-preview";
import type { PreconCover, PreconSectionMeta } from "./precon-types";

type Step = "cover" | "sections" | "review";
const STEPS: Step[] = ["cover", "sections", "review"];

const EMPTY_COVER: PreconCover = {
  title: "",
  project_description: "",
  report_subtitle: "",
  revisions: [],
};

/**
 * Merge a possibly-partial/legacy draft cover over a complete default so a
 * malformed draft (e.g. `{}` or a missing `revisions`) can never put
 * `undefined` into cover state. The title falls back to the project name only
 * when the hydrated title is empty.
 */
function hydrateCover(
  draftCover: Partial<PreconCover> | undefined,
  projectName: string,
): PreconCover {
  const merged: PreconCover = { ...EMPTY_COVER, ...draftCover };
  return {
    ...merged,
    title: merged.title || projectName,
    revisions: Array.isArray(draftCover?.revisions) ? draftCover.revisions : [],
  };
}

/** Flatten a field-error map (`{ "prepared_by.name": ["..."] }`) to messages. */
function coverErrorMessages(
  errors: PreconFieldErrors["cover"] | null,
): string[] {
  return errors ? Object.values(errors).flat() : [];
}

/** Flatten per-section errors (`{ 0: { plan_id: ["..."] } }`) to messages. */
function sectionErrorMessages(
  errors: PreconFieldErrors["sections"] | null,
): string[] {
  if (!errors) return [];
  return Object.values(errors).flatMap((fields) =>
    Object.values(fields).flat(),
  );
}

/** Strip the re-derived `plan_id`/`precon_date` so only editable fields persist. */
function toSectionDrafts(
  sectionMeta: Record<string, PreconSectionMeta>,
): Record<string, PreconSectionDraft> {
  const out: Record<string, PreconSectionDraft> = {};
  for (const [planId, meta] of Object.entries(sectionMeta)) {
    out[planId] = {
      level: meta.level,
      grid_lines: meta.grid_lines,
      location: meta.location,
      notes: meta.notes,
    };
  }
  return out;
}

export function PreconReportBuilder({
  projectId,
  project,
}: {
  projectId: string;
  project: ProjectDetail;
}) {
  const navigate = useNavigate();
  const { can } = usePermissions({
    owner: project.owner.id,
    my_roles: project.my_roles,
  });
  const selection = useMemo(() => readPreconSelection(projectId), [projectId]);
  // `isFetched` is TanStack's "settled" signal: false until the first fetch
  // resolves, then true on success OR error. We gate the once-only section init
  // on it so `order` isn't locked to the plan-id fallback ordering before real
  // plan names arrive (see the init effect below).
  const { data: plansData, isFetched: plansSettled } =
    useProjectPlans(projectId);
  const plans = useMemo(() => plansData?.plans ?? [], [plansData]);
  const isOnline = useOnlineStatus();
  const generate = useGenerateReport(projectId);

  // Hydrate once on mount from the per-project draft, defensively merging over
  // complete defaults so a partial/legacy draft can't leak `undefined` in.
  const draft = useMemo(() => readPreconDraft(projectId), [projectId]);
  // A saved draft "prepared by" name takes precedence over the profile prefill.
  const draftHadPreparedByName = Boolean(draft?.cover?.prepared_by?.name?.trim());

  // `isFetched` (settled: success OR error) also gates the redirect-out below:
  // `can()` resolves the owner check from this query, so judging permissions
  // before it settles would misread "still loading" as "no permission".
  const { data: profile, isFetched: profileSettled } = useCurrentUser();

  const [step, setStep] = useState<Step>("cover");
  // Furthest step the user has reached. Visited steps render as stepper
  // buttons (jump back / re-forward); unvisited ones stay inert — forward
  // progress goes through Next so a step is never skipped.
  const [maxVisitedIdx, setMaxVisitedIdx] = useState(0);
  // Read-only assembled-document preview (US-008). An overlay (Sheet) over the
  // unchanged form, so dismissing it restores the step + scroll automatically —
  // the form is never unmounted.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [headerBanner, setHeaderBanner] = useState(
    () => draft?.headerBanner ?? "",
  );
  const [cover, setCover] = useState<PreconCover>(() =>
    hydrateCover(draft?.cover, project.name),
  );
  const [sectionMeta, setSectionMeta] = useState<
    Record<string, PreconSectionMeta>
  >({});
  const [order, setOrder] = useState<string[]>([]);
  // Per-photo captions, keyed by `image_id` (not record id) — captions are
  // per-photo and report-only. Holds user edits only; the establishing shot's
  // seed (the record remark) and the blank default for a record's other photos
  // are applied at read time (UI rows + payload assembly), mirroring the
  // dialog's derive-don't-store remark pattern.
  const [captions, setCaptions] = useState<Record<string, string>>({});

  // Submission + validation-routing state. `submitting` mirrors the dialog's
  // loading lock; `genericError` is the catch-all banner shown on Review; the
  // field-error maps drive per-step banners after the backend rejects a build.
  const [submitting, setSubmitting] = useState(false);
  const [genericError, setGenericError] = useState<string | null>(null);
  const [coverErrors, setCoverErrors] = useState<
    PreconFieldErrors["cover"] | null
  >(null);
  const [sectionErrors, setSectionErrors] = useState<
    PreconFieldErrors["sections"] | null
  >(null);

  // Prefill the "Prepared by" name from the current user's profile (US-002 AC2).
  // useCurrentUser is async, so a lazy state initializer would miss it — seed via
  // a one-shot effect once the profile lands. Fire at most once, and skip when a
  // saved draft already supplied a name. The functional updater re-checks the
  // live field so a user edit made before the profile resolved is never clobbered
  // (and we never create a nameless prepared_by, preserving keepSignatory's drop).
  const preparedByPrefilled = useRef(false);
  useEffect(() => {
    if (preparedByPrefilled.current || draftHadPreparedByName) return;
    const profileName = profile
      ? `${profile.first_name} ${profile.last_name}`.trim()
      : "";
    if (!profileName) return;
    preparedByPrefilled.current = true;
    // One-shot seed of cover state from async profile data; ref-guarded and a
    // no-op when the name is already set, so it cannot loop or clobber edits.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCover((prev) => {
      if (prev.prepared_by?.name.trim()) return prev;
      return {
        ...prev,
        prepared_by: {
          designation: prev.prepared_by?.designation ?? "",
          name: profileName,
        },
      };
    });
  }, [profile, draftHadPreparedByName]);

  // Persist cover/header/section metadata to the per-project draft on change.
  // Skip when the builder is bouncing out (no selection / no permission) so a
  // project the user never built for doesn't get a default draft written.
  // `toSectionDrafts` strips the re-derived plan_id/precon_date so only the
  // remembered editable fields (level/grid/location/notes) are persisted.
  const canBuild = Boolean(selection) && can("report:Create");
  useEffect(() => {
    if (!canBuild) return;
    writePreconDraft(projectId, {
      headerBanner,
      cover,
      sectionMeta: toSectionDrafts(sectionMeta),
    });
  }, [canBuild, projectId, headerBanner, cover, sectionMeta]);

  // Redirect out if there is nothing to build or no permission — but only
  // once the current-user query has settled. On a cold load the builder can
  // mount before /users/me/ resolves (un-nested route: no project layout has
  // warmed it), and bouncing on that transient `can() === false` would eject
  // a legitimate owner. Until settled the builder renders nothing.
  useEffect(() => {
    if (profileSettled && !canBuild) {
      void navigate({
        to: "/projects/$projectId/records",
        params: { projectId },
        replace: true,
      });
    }
  }, [profileSettled, canBuild, navigate, projectId]);

  const derived = useMemo(
    () =>
      selection
        ? derivePreconSections(selection, plans)
        : { sections: [], unlocated: [] },
    [selection, plans],
  );

  // Initialise section order + per-plan metadata exactly once, when `derived`
  // first yields sections. `plans` loads async, so `derived.sections` is empty
  // on the first render(s) and populates later — a lazy state initializer would
  // miss it. The ref guard fires the body a single time and a later re-render
  // (e.g. the persist effect rewriting localStorage) NEVER resets a user's
  // reorder or field edits. Loop-safety: this writes `sectionMeta`, the persist
  // effect reads it, but the guard is a plain ref (not derived from the draft),
  // so the persisted write can't re-trigger init — no init→persist→re-init.
  //
  // Wait for the plans query to settle before locking `order`: until then
  // `derivePreconSections` titles each section with the raw plan-id fallback
  // and sorts by it, so initialising early would freeze id-order instead of
  // plan-name order. `isFetched` is true after the first fetch resolves —
  // success OR error — so an empty-plans or errored query still inits once
  // (id-fallback names there are acceptable degradation), never hanging.
  const sectionsInitialised = useRef(false);
  useEffect(() => {
    if (sectionsInitialised.current) return;
    if (!plansSettled) return;
    if (derived.sections.length === 0) return;
    sectionsInitialised.current = true;

    const initialOrder = derived.sections.map((s) => s.plan_id);
    const initialMeta: Record<string, PreconSectionMeta> = {};
    for (const section of derived.sections) {
      // Level/Grid/Location/Notes are remembered per project (draft); Pre-con
      // date is re-derived from this selection's capture dates (US-004, never
      // persisted). `draft` is memoized on projectId — a stable mount read.
      const saved = draft?.sectionMeta?.[section.plan_id];
      initialMeta[section.plan_id] = {
        plan_id: section.plan_id,
        precon_date: prefillPreconDate(section),
        // Default level to the plan name (not ""); a saved draft value takes
        // precedence — the default only fires on first load with no saved value.
        level: saved?.level ?? section.planName,
        grid_lines: saved?.grid_lines ?? "",
        location: saved?.location ?? "",
        notes: saved?.notes ?? [],
      };
    }
    // One-shot seed from async-derived data; ref-guarded so it can neither loop
    // nor clobber later edits. The lint rule flags the effect once at the first
    // setter, so a single disable covers both state writes below.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOrder(initialOrder);
    setSectionMeta(initialMeta);
  }, [derived, draft, plansSettled]);

  if (!canBuild) return null;

  const idx = STEPS.indexOf(step);
  // Single entry point for every step change so `maxVisitedIdx` can never go
  // stale — Next, Back, stepper clicks, and backend error routing all count
  // the destination as visited.
  const visitStep = (next: Step) => {
    setStep(next);
    setMaxVisitedIdx((prev) => Math.max(prev, STEPS.indexOf(next)));
  };
  const goNext = () => visitStep(STEPS[Math.min(idx + 1, STEPS.length - 1)]);
  const goBack = () => visitStep(STEPS[Math.max(idx - 1, 0)]);
  // Header close and footer Cancel share one exit: back to the records grid.
  const exitToRecords = () =>
    navigate({
      to: "/projects/$projectId/records",
      params: { projectId },
    });

  // A survey needs at least one plan-scoped section (the backend rejects an
  // empty `sections[]`); offline can't round-trip; and a click is ignored while
  // a submission is already in flight. Same gate the Review block surfaces.
  const hasSections = derived.sections.length > 0;
  const canGenerate = hasSections && isOnline && !submitting;

  const handleGenerate = () => {
    setSubmitting(true);
    setGenericError(null);
    setCoverErrors(null);
    setSectionErrors(null);

    const payload = buildPreconPayload({
      reportName: cover.title,
      headerBanner,
      cover,
      orderedSections: order.map((id) => sectionMeta[id]).filter(Boolean),
      // `handleGenerate` only runs past the `!canBuild` guard, which requires a
      // non-null selection; the `?? []` satisfies the type without changing
      // behaviour (it can never actually be null here).
      records: selection ?? [],
      captions,
    });

    generate.mutate(payload, {
      onSuccess: () => {
        // The mutation already optimistically inserted the PENDING row and
        // primed the list/detail caches; we just drop the hand-off selection
        // and land on the reports list (US-006).
        clearPreconSelection(projectId);
        void navigate({
          to: "/projects/$projectId/reports",
          params: { projectId },
        });
      },
      onError: (error) => {
        Sentry.captureException(error);
        setSubmitting(false);

        if (!(error instanceof ApiError)) {
          setGenericError(
            error instanceof Error && error.message
              ? error.message
              : "An unexpected error occurred. Please try again.",
          );
          return;
        }

        // Route a structured precon validation error back to the step/field it
        // concerns (cover vs. a specific section); fall back to a generic
        // banner on Review for anything else (transport, non-precon 4xx/5xx).
        const parsed = parsePreconErrors(error.body);
        const hasCover = parsed.cover && Object.keys(parsed.cover).length > 0;
        const hasSectionErrs =
          parsed.sections && Object.keys(parsed.sections).length > 0;

        if (hasCover) {
          setCoverErrors(parsed.cover ?? null);
          visitStep("cover");
        } else if (hasSectionErrs) {
          setSectionErrors(parsed.sections ?? null);
          visitStep("sections");
        } else {
          setGenericError(
            parsed.generic ??
              `Failed to generate report (HTTP ${error.status}).`,
          );
        }
      },
    });
  };

  return (
    // The builder renders outside the project shell and owns the screen
    // (`.h-app-screen`, see specs/conventions/safe-area-bottom.md § "App shell
    // height"); <main> below is the page's only scroll region, keeping the
    // stepper and footer always visible.
    <div className="flex h-app-screen flex-col">
      <header
        className="flex shrink-0 items-center justify-between border-b px-6 pb-4"
        // Full-screen surface: own the top safe area (notch/status bar) —
        // there is no project header above us to absorb it.
        style={{ paddingTop: "max(1rem, env(safe-area-inset-top, 0px))" }}
      >
        <h1 className="text-lg font-semibold">Pre-Condition Survey</h1>
        <div className="flex items-center gap-1">
          {/* Preview is available on every step — a read-only look at the
              assembled document (US-008). Sits beside the close affordance. */}
          <Button
            variant="ghost"
            size="icon"
            aria-label="Preview report"
            onClick={() => setPreviewOpen(true)}
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Close builder"
            onClick={exitToRecords}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <nav
        aria-label="Steps"
        className="flex shrink-0 gap-4 border-b px-6 py-2 text-sm"
      >
        {STEPS.map((s, i) => {
          const label = s[0].toUpperCase() + s.slice(1);
          if (s === step) {
            return (
              <span
                key={s}
                aria-current="step"
                className="font-semibold text-primary"
              >
                {i + 1}. {label}
              </span>
            );
          }
          // A visited step is a real button (jump to it), ticked because the
          // user has been there and moved on; unvisited steps stay inert.
          if (i <= maxVisitedIdx) {
            return (
              <button
                key={s}
                type="button"
                onClick={() => visitStep(s)}
                className="flex items-center gap-1 text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              >
                <Check aria-hidden className="h-3.5 w-3.5" />
                {label}
              </button>
            );
          }
          return (
            <span key={s} className="text-muted-foreground">
              {i + 1}. {label}
            </span>
          );
        })}
      </nav>

      {submitting ? (
        // Mirror the dialog's loading lock: the wizard is non-dismissable
        // (no footer) until the mutation resolves.
        <main className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 px-6 py-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <p className="text-base font-medium text-muted-foreground">
            Submitting your report…
          </p>
        </main>
      ) : (
        <>
          <main className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
            {step === "cover" && (
              <div className="space-y-4">
                <ErrorBanner messages={coverErrorMessages(coverErrors)} />
                <PreconCoverStep
                  cover={cover}
                  onCoverChange={setCover}
                  headerBanner={headerBanner}
                  onHeaderBannerChange={setHeaderBanner}
                  logo={project.logo}
                />
              </div>
            )}
            {step === "sections" && (
              <div className="space-y-4">
                <ErrorBanner
                  messages={sectionErrorMessages(sectionErrors)}
                />
                <PreconSectionsStep
                  projectId={projectId}
                  derived={derived}
                  order={order}
                  onOrderChange={setOrder}
                  plans={plans}
                  sectionMeta={sectionMeta}
                  onSectionMetaChange={(planId, next) =>
                    setSectionMeta((prev) => ({ ...prev, [planId]: next }))
                  }
                  captions={captions}
                  onCaptionChange={(imageId, value) =>
                    setCaptions((prev) => ({ ...prev, [imageId]: value }))
                  }
                />
              </div>
            )}
            {step === "review" && (
              <div className="space-y-4">
                {genericError && (
                  <ErrorBanner messages={[genericError]} />
                )}
                {!isOnline && (
                  <div className="flex items-start gap-3 rounded-md border border-border bg-muted px-4 py-3 text-muted-foreground">
                    <CloudOff className="mt-0.5 h-4 w-4 shrink-0" />
                    <span className="flex-1 text-sm">
                      You&apos;re offline. Reconnect to generate this report —
                      your selections are saved.
                    </span>
                  </div>
                )}
                <PreconReviewStep
                  derived={derived}
                  order={order}
                  cover={cover}
                />
              </div>
            )}
          </main>

          <footer
            className="flex shrink-0 justify-between border-t px-6 pt-4"
            // Full-screen surface: own the bottom safe area (home indicator)
            // per safe-area-bottom.md § "Modal / dialog full-screen surfaces".
            style={{
              paddingBottom: "max(1rem, env(safe-area-inset-bottom, 0px))",
            }}
          >
            <Button
              variant="outline"
              onClick={step === "cover" ? exitToRecords : goBack}
            >
              {step === "cover" ? "Cancel" : "Back"}
            </Button>
            {step === "review" ? (
              <Button onClick={handleGenerate} disabled={!canGenerate}>
                Generate
              </Button>
            ) : (
              <Button onClick={goNext}>Next</Button>
            )}
          </footer>
        </>
      )}

      {/* Read-only assembled-document preview (US-008). One renderer in a
          Sheet: full-screen on mobile (SheetContent is w-full on small
          screens), a wide right-side panel on desktop (md:max-w-2xl+). It
          overlays the unchanged form, so closing restores the step + scroll
          with nothing unmounted. Derived from the same state Generate sends. */}
      <Sheet open={previewOpen} onOpenChange={setPreviewOpen}>
        <SheetContent
          side="right"
          className="w-full gap-0 p-0 sm:max-w-md md:max-w-2xl lg:max-w-3xl"
        >
          <SheetTitle className="sr-only">Report preview</SheetTitle>
          <SheetDescription className="sr-only">
            A read-only, layout-approximate preview of the assembled
            Pre-Condition Survey. The generated PDF is the source of truth.
          </SheetDescription>
          <PreconPreview
            cover={cover}
            headerBanner={headerBanner}
            logo={project.logo}
            derived={derived}
            order={order}
            plans={plans}
            sectionMeta={sectionMeta}
            captions={captions}
          />
        </SheetContent>
      </Sheet>
    </div>
  );
}

/** Destructive banner listing one or more backend validation messages. */
function ErrorBanner({ messages }: { messages: string[] }) {
  if (messages.length === 0) return null;
  return (
    <div className="flex items-start gap-3 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-destructive">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex-1 space-y-1 text-sm">
        {messages.map((m, i) => (
          <p key={i}>{m}</p>
        ))}
      </div>
    </div>
  );
}
